import { useEffect, useState } from 'react'
import type { DailyReportSourceKey } from 'api/generated'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { ThemeSwitch } from 'components/theme-switch'
import { Button } from 'components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { isBannerManager, isCensorManager } from 'helpers/authentication'
import { ExternalLink } from 'lucide-react'
import { fetchDailyReportSources } from 'store/dailyReport/dailyReportSlice'
import { useAppDispatch } from 'store/store'
import { DailyReportExplorer } from 'ui/components/dashboard/DailyReportExplorer'
import { LiveNumbers } from 'ui/components/dashboard/LiveNumbers'
import { ReportOverview } from 'ui/components/dashboard/ReportOverview'

const LINKS = [
  {
    label: 'Analytics',
    url: 'https://analytics.google.com/analytics/web/#/p291660364/reports/intelligenthome',
  },
  {
    label: 'File di controllo',
    url: 'https://docs.google.com/spreadsheets/d/1R36ypT4F5GJX2KDYrGBBWrhdlr2k_yu-DdTrxQ2EdWU/edit?gid=0#gid=0',
  },
  { label: 'Advertising', url: 'https://adkontrol.adkaora.com/' },
]

const DashboardPage = () => {
  const dispatch = useAppDispatch()
  // The daily report API is admin only (revenue, subscriptions, channels)
  const isAdmin = !isBannerManager() && !isCensorManager()
  const [tab, setTab] = useState('overview')
  const [detailSource, setDetailSource] =
    useState<DailyReportSourceKey>('backoffice')

  useEffect(() => {
    if (isAdmin) dispatch(fetchDailyReportSources())
  }, [dispatch, isAdmin])

  const openDetail = (source: DailyReportSourceKey) => {
    setDetailSource(source)
    setTab('detail')
  }

  return (
    <>
      {/* ===== Top Heading ===== */}
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
          <ThemeSwitch />
        </div>
      </Header>

      {/* ===== Main ===== */}
      <Main>
        <div className='mb-2 flex items-center justify-between space-y-2'>
          <h1 className='text-2xl font-bold tracking-tight'>Dashboard</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab} className='space-y-4'>
          <div className='flex w-full flex-wrap items-center gap-2 overflow-x-auto pb-2'>
            <TabsList>
              <TabsTrigger value='overview'>Panoramica</TabsTrigger>
              {isAdmin && <TabsTrigger value='detail'>Dettaglio</TabsTrigger>}
            </TabsList>
            <div className='ms-auto flex flex-wrap gap-1'>
              {LINKS.map((link) => (
                <Button key={link.label} variant='ghost' size='sm' asChild>
                  <a href={link.url} target='_blank' rel='noreferrer'>
                    {link.label} <ExternalLink className='h-3 w-3' />
                  </a>
                </Button>
              ))}
            </div>
          </div>

          <TabsContent value='overview' className='space-y-8'>
            <section className='space-y-3'>
              <div>
                <h2 className='text-lg font-semibold'>In tempo reale</h2>
                <p className='text-sm text-muted-foreground'>
                  Calcolati adesso dal backend, aggiornati ogni minuto.
                </p>
              </div>
              <LiveNumbers />
            </section>

            {isAdmin && <ReportOverview onOpenDetail={openDetail} />}
          </TabsContent>

          {isAdmin && (
            <TabsContent value='detail'>
              <p className='mb-4 text-sm text-muted-foreground'>
                Tutte le metriche di una fonte del report giornaliero, giorno
                per giorno: sono gli stessi dati che legge il server MCP.
              </p>
              <DailyReportExplorer
                source={detailSource}
                onSourceChange={setDetailSource}
              />
            </TabsContent>
          )}
        </Tabs>
      </Main>
    </>
  )
}

export default DashboardPage
